<script setup lang="ts">
    import BetDialogEvent from '@/pages/core/betDialog/BetDialogEvent.vue';
    import BetOrderDialogInfo from '@/pages/core/betDialog/BetOrderDialogInfo.vue';
    import { useTheme } from 'vuetify';
    import { BetOrderStatus, EventFeedType } from '@/constants';
    import { useAppStore } from '@/stores/app';

    const { id } = defineProps<{
        id: string;
    }>();

    const appStore = useAppStore();
    const bettingData = appStore.betFeedData[id];

    const emit = defineEmits(['confirmed']);

    const dialog = ref(false);
    const theme = useTheme();
    const { t } = useI18n();

    function open() {
        dialog.value = true;
    }

    function close() {
        dialog.value = false;
    }

    defineExpose({ open, close });

    function cancel() {
        close();
    }

    function confirm() {
        emit('confirmed', close);
    }

    const duplicateOrders = computed(() => {
        const isLive = bettingData.eventFeedType === EventFeedType.Live;

        return appStore.orders.filter(
            (order) =>
                order.customEventId === bettingData.customEventId &&
                order.statusId !== BetOrderStatus.Closed &&
                order.betSelectionId === bettingData.betSelectionId &&
                Number(order.isLive) === Number(isLive)
        );
    });
</script>

<template>
    <v-dialog v-model="dialog" width="420" persistent @keydown.esc="cancel" @keydown.enter="cancel" :scrim="true">
        <v-card class="bet-dialog">
            <v-toolbar density="compact" flat :color="theme.name.value === 'dark' ? '#1e1f22' : 'white'">
                <v-toolbar-title class="text-h5">
                    <v-icon color="orange" class="pb-1" size="24" :icon="mdiAlert" />
                    {{ t('betDialog.orderConfirmDuplicateTitle') }}
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text class="px-5 pt-2 font14">
                <v-container class="pa-3">
                    <v-row class="pt-2">
                        <span>{{ t('betDialogConfirm.orderConfirmDuplicateText1', { count: duplicateOrders.length, orderCount: duplicateOrders.length }) }}:</span>
                    </v-row>
                    <v-row class="h-22 d-flex flex-column justify-center pt-1">
                        <BetDialogEvent :id="id"></BetDialogEvent>
                        <v-spacer></v-spacer>
                    </v-row>

                    <template v-for="order in duplicateOrders.slice(0, 8)" :key="order.id">
                        <v-row class="pt-1">
                            <BetOrderDialogInfo :orderId="order.id" :odds="order.odds" :stake="order.stake" />
                        </v-row>
                    </template>

                    <v-row v-if="duplicateOrders.length > 8" class="pt-1">
                        <v-chip class="info-line" label style="padding: 0 5px; font-size: 16.25px" :color="theme.name.value === 'dark' ? '#323338' : '#e3e3e3'">
                            <span>+{{ duplicateOrders.length - 8 }} more</span>
                        </v-chip>
                    </v-row>

                    <v-row class="d-flex flex-column align-start justify-center pt-2 pb-1">
                        <span>{{ t('betDialogConfirm.orderConfirmDuplicateText2') }}</span>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="pt-0">
                <v-spacer></v-spacer>
                <v-btn tag="a" density="default" color="grey" variant="text" @click="cancel()">
                    {{ t('action.cancel') }}
                </v-btn>
                <v-btn :disabled="!bettingData.isActive" density="default" color="tbGreen" elevation="0" class="text-white" tag="a" variant="flat" @click="confirm()">
                    {{ t('action.order') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
    .info-line {
        font-weight: 500;
    }
    .theme--light .info-line.v-chip {
        background-color: #f7f7f7 !important;
        border-color: #e3e3e3 !important;
        border-width: 1px !important;
        border-style: solid !important;
    }

    .theme--light .info-line.v-chip:before {
        background-color: #f7f7f7;
    }

    .theme--dark .info-line.v-chip {
        background-color: #323338 !important;
    }

    .theme--dark .info-line.v-chip:before {
        background-color: #323338;
    }

    .v-theme--dark .v-card.bet-dialog {
        border: 1px solid #404148;
    }

    .v-theme--light .v-card.bet-dialog {
        border: thin solid rgba(0, 0, 0, 0.12);
    }
</style>
